import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Plus, User, QrCode, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { first_name: "", last_name: "", grade: "", birth_date: "", school_type: "Homeschool", shirt_size: "", allergies: "", accommodations: "", emergency_info: "" };
const SCHOOL_TYPES = ["Homeschool", "Public", "Private", "Charter", "Other"];

export default function Students() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error("First and last name are required."); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/students/${editId}`, form);
      else await api.post("/students", form);
      toast.success(editId ? "Student updated." : "Student added.");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/students/${id}`); toast.success("Student removed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy">My Students</h1>
          <p className="text-slateBrand">Your H.E.A. Explorers.</p>
        </div>
        <Button data-testid="students-add-btn" onClick={openNew} className="bg-brandblue font-display font-semibold hover:bg-blue-700"><Plus className="mr-2 h-4 w-4" />Add Child</Button>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {students.map((s) => (
          <div key={s.id} data-testid={`student-card-${s.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brandblue/10"><User className="h-6 w-6 text-brandblue" /></div>
              <div>
                <div className="font-display font-bold text-navy">{s.first_name} {s.last_name}</div>
                <div className="text-xs text-slateBrand">Grade {s.grade} {s.age ? `• Age ${s.age}` : ""} • {s.school_type}</div>
              </div>
            </div>
            {(s.allergies || s.accommodations) && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-alertBrand/5 p-2 text-xs text-alertBrand">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{[s.allergies && `Allergies: ${s.allergies}`, s.accommodations && `Accommodations: ${s.accommodations}`].filter(Boolean).join(" · ")}</span>
              </div>
            )}
            <div className="mt-3 text-xs text-slateBrand">ID: <span className="font-mono font-semibold text-navy">{s.student_id}</span></div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" data-testid={`student-pass-${s.id}`} onClick={() => navigate(`/explorer-pass/${s.id}`)} className="flex-1"><QrCode className="mr-1 h-4 w-4" />Pass</Button>
              <Button size="sm" variant="outline" data-testid={`student-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`student-delete-${s.id}`} className="text-alertBrand"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Remove {s.first_name}?</AlertDialogTitle><AlertDialogDescription>This will remove the student from your family. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid={`student-delete-confirm-${s.id}`} onClick={() => remove(s.id)} className="bg-alertBrand hover:bg-alertBrand/90">Remove</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {students.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <User className="mx-auto h-10 w-10 text-brandblue" />
            <p className="mt-3 text-slateBrand">Add your first child to start registering for experiences.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editId ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First name</Label><Input data-testid="student-form-first" className="mt-1.5 h-11" value={form.first_name} onChange={set("first_name")} /></div>
            <div><Label>Last name</Label><Input data-testid="student-form-last" className="mt-1.5 h-11" value={form.last_name} onChange={set("last_name")} /></div>
            <div><Label>Birth date</Label><Input data-testid="student-form-dob" type="date" className="mt-1.5 h-11" value={form.birth_date} onChange={set("birth_date")} /></div>
            <div><Label>Grade</Label><Input data-testid="student-form-grade" className="mt-1.5 h-11" value={form.grade} onChange={set("grade")} placeholder="5th" /></div>
            <div>
              <Label>School type</Label>
              <Select value={form.school_type} onValueChange={(v) => setForm({ ...form, school_type: v })}>
                <SelectTrigger data-testid="student-form-schooltype" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{SCHOOL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Shirt size</Label><Input data-testid="student-form-shirt" className="mt-1.5 h-11" value={form.shirt_size} onChange={set("shirt_size")} placeholder="Youth M" /></div>
            <div className="col-span-2"><Label>Allergies</Label><Input data-testid="student-form-allergies" className="mt-1.5 h-11" value={form.allergies} onChange={set("allergies")} /></div>
            <div className="col-span-2"><Label>Accommodations</Label><Textarea data-testid="student-form-accommodations" className="mt-1.5" value={form.accommodations} onChange={set("accommodations")} /></div>
            <div className="col-span-2"><Label>Emergency info</Label><Textarea data-testid="student-form-emergency" className="mt-1.5" value={form.emergency_info} onChange={set("emergency_info")} /></div>
          </div>
          <Button data-testid="student-form-save" disabled={saving} onClick={save} className="mt-2 h-12 w-full bg-brandblue font-display font-semibold hover:bg-blue-700">{saving ? "Saving…" : "Save Student"}</Button>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
